import moment from 'moment';
import {LogEntryAuthorTypes, LogEntryTypes} from '@/enums/LogEntryEnums';

/** Client side categories of the timeline filter bar */
export type LogCategory = 'all' | 'notes' | 'calls' | 'mails' | 'whatsapp' | 'earlyWarning' | 'other';

export type LogTypeMeta = {
    /** Short German label shown in the type tag */
    label: string;
    icon: string;
    /** Tone class of modern.css (tone-good, tone-info, ...) */
    tone: string;
    /** Color of the dot on the timeline rail */
    color: string;
    category: Exclude<LogCategory, 'all'>;
};

const TYPE_META: Record<LogEntryTypes, LogTypeMeta> = {
    [LogEntryTypes.COMMON]: {label: 'Notiz', icon: 'mdi-note-text-outline', tone: 'tone-info', color: '#1976D2', category: 'notes'},
    [LogEntryTypes.QUALITY_MANAGEMENT_TEACHER]: {label: 'QM-Lehrkraftgespräch', icon: 'mdi-poll', tone: 'tone-teal', color: '#009688', category: 'notes'},
    [LogEntryTypes.PHONE]: {label: 'Gesprächsprotokoll', icon: 'mdi-phone-message-outline', tone: 'tone-warn', color: '#F59E0B', category: 'calls'},
    [LogEntryTypes.PLACETEL_CALL]: {label: 'Anruf über Placetel', icon: 'mdi-phone-in-talk-outline', tone: 'tone-info', color: '#0A9EDE', category: 'calls'},
    [LogEntryTypes.VAPI_CALL]: {label: 'KI-Anruf', icon: 'mdi-robot-outline', tone: 'tone-purple', color: '#7C4DFF', category: 'calls'},
    [LogEntryTypes.MESSENGER_MAIL]: {label: 'E-Mail', icon: 'mdi-email-outline', tone: 'tone-info', color: '#3F51B5', category: 'mails'},
    [LogEntryTypes.MESSENGER_WHATS_APP]: {label: 'WhatsApp', icon: 'mdi-whatsapp', tone: 'tone-good', color: '#25D366', category: 'whatsapp'},
    [LogEntryTypes.TERMINATION]: {label: 'Kündigung', icon: 'mdi-file-cancel-outline', tone: 'tone-bad', color: '#E53935', category: 'other'},
    [LogEntryTypes.RETURN_LABEL]: {label: 'Rücksendeetikett', icon: 'mdi-package-variant-closed', tone: 'tone-muted', color: '#78909C', category: 'other'},
    [LogEntryTypes.RETURNING_DONE]: {label: 'Rücklauf erfolgt', icon: 'mdi-package-variant-closed-check', tone: 'tone-good', color: '#43A047', category: 'other'},
    [LogEntryTypes.RETURNING_DELAYED]: {label: 'Rücklauf verzögert', icon: 'mdi-clock-alert-outline', tone: 'tone-warn', color: '#FB8C00', category: 'other'},
    [LogEntryTypes.EARLY_WARNING]: {label: 'Frühwarnung', icon: 'mdi-bell-alert-outline', tone: 'tone-warn', color: '#E65100', category: 'earlyWarning'},
    [LogEntryTypes.COURSE]: {label: 'Kursprotokoll', icon: 'mdi-school-outline', tone: 'tone-purple', color: '#7E57C2', category: 'other'},
};

const FALLBACK_META: LogTypeMeta = {label: 'Eintrag', icon: 'mdi-text-box-outline', tone: 'tone-muted', color: '#90A4AE', category: 'other'};

export function getLogTypeMeta(type: LogEntryTypes | string | undefined): LogTypeMeta {
    return (type && TYPE_META[type as LogEntryTypes]) || FALLBACK_META;
}

export const LOG_CATEGORIES: Array<{ value: LogCategory; text: string; icon: string }> = [
    {value: 'all', text: 'Alle', icon: 'mdi-format-list-bulleted'},
    {value: 'notes', text: 'Notizen', icon: 'mdi-note-text-outline'},
    {value: 'calls', text: 'Anrufe', icon: 'mdi-phone-outline'},
    {value: 'mails', text: 'Mails', icon: 'mdi-email-outline'},
    {value: 'whatsapp', text: 'WhatsApp', icon: 'mdi-whatsapp'},
    {value: 'earlyWarning', text: 'Frühwarnung', icon: 'mdi-bell-alert-outline'},
    {value: 'other', text: 'Sonstiges', icon: 'mdi-dots-horizontal-circle-outline'},
];

/** All log entry types that belong to the given category */
export function getTypesOfCategory(category: LogCategory): LogEntryTypes[] {
    return (Object.keys(TYPE_META) as LogEntryTypes[])
        .filter((type) => category === 'all' || TYPE_META[type].category === category);
}

/** Maps Vuetify color names used in enums to the soft tone classes of modern.css */
export function colorToTone(color: string | undefined): string {
    switch ((color || '').split(' ')[0]) {
        case 'success':
        case 'green':
            return 'tone-good';
        case 'warning':
        case 'orange':
        case 'amber':
            return 'tone-warn';
        case 'error':
        case 'red':
            return 'tone-bad';
        case 'teal':
            return 'tone-teal';
        case 'info':
        case 'blue':
        case 'primary':
            return 'tone-info';
        default:
            return 'tone-muted';
    }
}

/** Plain text of an HTML snippet (used for search and previews) */
export function htmlToText(html: string | undefined | null): string {
    if (!html) {
        return '';
    }
    const el = document.createElement('div');
    el.innerHTML = html;
    return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
}

/** Human readable duration of a call given in seconds */
export function formatDuration(seconds: number | undefined | null): string {
    if (!seconds) {
        return '';
    }
    if (seconds > 3_600) {
        return `${Math.floor(seconds / 3_600)}h ${Math.floor((seconds % 3_600) / 60)}m`;
    }
    if (seconds > 60) {
        return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
    }
    return `${Math.floor(seconds)}s`;
}

type PersonLike = { firstName?: string; lastName?: string; name?: string };

/** Name of the author of a log entry, or a describing fallback if it is not populated */
export function getLogAuthorName(log: { author?: unknown; authorType?: string }): string {
    const author = log.author as PersonLike | undefined | null;
    if (author && typeof author === 'object' && (author.firstName || author.lastName)) {
        return `${author.firstName || ''} ${author.lastName || ''}`.trim();
    }
    if (author && typeof author === 'object' && typeof author.name === 'string' && author.name) {
        return author.name;
    }
    switch (log.authorType) {
        case LogEntryAuthorTypes.SYSTEM:
            return 'System';
        case LogEntryAuthorTypes.MESSENGER_ACCOUNT:
            return 'Messenger';
        case LogEntryAuthorTypes.INSTITUTION:
            return 'Standort';
        case LogEntryAuthorTypes.SALES_PARTNER:
            return 'Vertriebspartner';
        default:
            return 'Unbekannter Nutzer';
    }
}

/** Date that is used for sorting and grouping a log entry */
export function getLogDate(log: { sortingDate?: Date | string; createdAt?: Date | string }): Date {
    return new Date(log.sortingDate ?? log.createdAt ?? Date.now());
}

/** Label of the day header for the given date: Heute, Gestern or the full date */
export function getDayLabel(date: Date): string {
    const day = moment(date);
    if (day.isSame(moment(), 'day')) {
        return 'Heute';
    }
    if (day.isSame(moment().subtract(1, 'day'), 'day')) {
        return 'Gestern';
    }
    return day.isSame(moment(), 'year') ? day.format('dddd, DD. MMMM') : day.format('dddd, DD. MMMM YYYY');
}

/** Text of a log entry that is searched by the client side search of the timeline */
export function getLogSearchText(log: { type?: string; payload?: any; author?: unknown; authorType?: string }): string {
    const payload = log.payload || {};
    const ref = payload.ref && typeof payload.ref === 'object' ? `${payload.ref.firstName || ''} ${payload.ref.lastName || ''}` : '';
    return [
        getLogTypeMeta(log.type).label,
        getLogAuthorName(log),
        htmlToText(payload.message),
        htmlToText(payload.messagePreview),
        payload.subject,
        payload.summary,
        payload.refString,
        ref,
        payload.topic,
        payload.assistant,
        payload.fromNumber,
        payload.toNumber,
    ].filter((el) => typeof el === 'string' && el).join(' ').toLowerCase();
}

/** Icon of an attachment by its content type */
export function getFileIcon(contentType: string | undefined): string {
    switch ((contentType || '').split('/')[0].toLowerCase()) {
        case 'image':
            return 'mdi-image';
        case 'video':
            return 'mdi-video';
        case 'audio':
            return 'mdi-music';
        case 'document':
        case 'text':
            return 'mdi-file-document';
        case 'archive':
            return 'mdi-archive';
        case 'code':
            return 'mdi-code-tags';
        case 'pdf':
            return 'mdi-file-pdf';
        case 'spreadsheet':
            return 'mdi-file-excel';
        case 'presentation':
            return 'mdi-file-powerpoint';
        default:
            return 'mdi-file';
    }
}

/** Texts of the direction of messenger entries (mail and WhatsApp) */
export function getDirectionMeta(direction: 'in' | 'out' | undefined): { text: string; description: string; icon: string } {
    return direction === 'in'
        ? {text: 'An dieses Profil', description: 'Übermittelt an dieses Profil', icon: 'mdi-arrow-right-bold'}
        : {text: 'Von diesem Profil', description: 'Empfangen von diesem Profil', icon: 'mdi-arrow-left-bold'};
}

/** Scrolls smoothly to the timeline entry with the given id */
export function scrollToLogEntry(id: string | undefined): void {
    if (!id) {
        return;
    }
    document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'center'});
}
